"use client";

import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@wtc/ui";
import { cn } from "@/lib/utils";

interface EnhancedTabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

interface EnhancedTabsListProps {
  className?: string;
  children: React.ReactNode;
}

interface EnhancedTabsTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  variant?: "blue" | "green" | "purple" | "orange" | "red";
  onClick?: (e: React.MouseEvent) => void;
}

interface EnhancedTabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

// Color variants for different tab themes - Enhanced for better visibility and organization
const tabVariants = {
  blue: {
    active:
      "data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-700 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-blue-200",
    hover: "hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 dark:hover:bg-blue-950/40 dark:hover:text-blue-300 dark:hover:border-blue-800",
    inactive: "bg-card-50 text-gray-700 border-gray-200 hover:bg-card-100 dark:text-gray-300 dark:border-gray-700",
  },
  green: {
    active:
      "data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:border-green-700 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-green-200",
    hover: "hover:bg-green-50 hover:text-green-700 hover:border-green-300 dark:hover:bg-green-950/40 dark:hover:text-green-300 dark:hover:border-green-800",
    inactive: "bg-card-50 text-gray-700 border-gray-200 hover:bg-card-100 dark:text-gray-300 dark:border-gray-700",
  },
  purple: {
    active:
      "data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:border-purple-700 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-purple-200",
    hover: "hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-950/40 dark:hover:text-purple-300 dark:hover:border-purple-800",
    inactive: "bg-card-50 text-gray-700 border-gray-200 hover:bg-card-100 dark:text-gray-300 dark:border-gray-700",
  },
  orange: {
    active:
      "data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:border-orange-700 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-orange-200",
    hover: "hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 dark:hover:bg-orange-950/40 dark:hover:text-orange-300 dark:hover:border-orange-800",
    inactive: "bg-card-50 text-gray-700 border-gray-200 hover:bg-card-100 dark:text-gray-300 dark:border-gray-700",
  },
  red: {
    active:
      "data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:border-red-700 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-red-200",
    hover: "hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-300 dark:hover:border-red-800",
    inactive: "bg-card-50 text-gray-700 border-gray-200 hover:bg-card-100 dark:text-gray-300 dark:border-gray-700",
  },
};

export function EnhancedTabs({
  defaultValue,
  value,
  onValueChange,
  className,
  children,
}: EnhancedTabsProps) {
  return (
    <Tabs
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      className={cn("w-full", className)}
    >
      {children}
    </Tabs>
  );
}

export function EnhancedTabsList({ className, children }: EnhancedTabsListProps) {
  return <TabsList className={cn("", className)}>{children}</TabsList>;
}

export function EnhancedTabsTrigger({
  value,
  className,
  children,
  variant = "blue",
  onClick,
  ...props
}: EnhancedTabsTriggerProps) {
  return (
    <TabsTrigger
      value={value}
      onClick={onClick}
      className={cn(
        // Professional tab styling
        "flex items-center gap-2 transition-all duration-200 ease-in-out",
        "bg-transparent border-none rounded-none",
        "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300",
        "data-[state=active]:text-card-foreground data-[state=active]:border-b-2 data-[state=active]:border-blue-500",
        "data-[state=active]:bg-card",
        "hover:bg-card-50/50 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-700",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        className
      )}
      {...props}
    >
      {children}
    </TabsTrigger>
  );
}

export function EnhancedTabsContent({ value, className, children }: EnhancedTabsContentProps) {
  return (
    <TabsContent value={value} className={cn("mt-6", className)}>
      {children}
    </TabsContent>
  );
}

// Export all components as a single object for convenience
export const EnhancedTabsComponents = {
  Tabs: EnhancedTabs,
  TabsList: EnhancedTabsList,
  TabsTrigger: EnhancedTabsTrigger,
  TabsContent: EnhancedTabsContent,
};
